/* eslint-disable camelcase */
import URI from 'jsuri';

import API from 'js/lib/api';

import adsTracker from 'bundles/event-tracking/lib';
import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import type ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import type { EnterpriseContractInfo } from 'bundles/payments/common/types';
import type { BillingTypeValues } from 'bundles/teams-v2/constants/billing';

export type UserData = {
  email_address: string;
  full_name: string;
  id?: number;
};

export const enterpriseLeadSourceTypes = {
  WEB_ENABLED_SALES: 'WEB_ENABLED_SALES',
  WEB_ENABLED_SALES_BILLING_INFO: 'WEB_ENABLED_SALES_BILLING_INFO',
  WEB_ENABLED_SALES_PLAN_DETAILS: 'WEB_ENABLED_SALES_PLAN_DETAILS',
  WEB_ENABLED_SALES_ACCOUNT_DETAILS: 'WEB_ENABLED_SALES_ACCOUNT_DETAILS',
} as const;

export type EnterpriseLeadSource = keyof typeof enterpriseLeadSourceTypes;

const enterpriseLeadsAPI = API('/api/enterpriseLeads.v1', { type: 'rest' });

export type EnterpriseLeadTransactionType =
  | 'STANDARD_CHECKOUT_STARTED'
  | 'PAID'
  | 'FREE_TRIAL_CHECKOUT_STARTED'
  | 'FREE_TRIAL_ACTIVE';

export const enterpriseLeadTransactionType: Record<EnterpriseLeadTransactionType, EnterpriseLeadTransactionType> = {
  STANDARD_CHECKOUT_STARTED: 'STANDARD_CHECKOUT_STARTED',
  PAID: 'PAID',
  FREE_TRIAL_CHECKOUT_STARTED: 'FREE_TRIAL_CHECKOUT_STARTED',
  FREE_TRIAL_ACTIVE: 'FREE_TRIAL_ACTIVE',
};

export type EnterpriseLeadOrganizationType = 'BUSINESS' | 'UNIVERSITY' | 'GOVERNMENT' | 'NON_PROFIT' | 'OTHER';
export const enterpriseLeadOrganizationType: Record<EnterpriseLeadOrganizationType, EnterpriseLeadOrganizationType> = {
  BUSINESS: 'BUSINESS',
  UNIVERSITY: 'UNIVERSITY',
  GOVERNMENT: 'GOVERNMENT',
  NON_PROFIT: 'NON_PROFIT',
  OTHER: 'OTHER',
};

export type EnterpriseLeadOrganizationSizeCategory =
  | 'SIZE_1_500'
  | 'SIZE_501_1000'
  | 'SIZE_1001_5000'
  | 'SIZE_5001_15000'
  | 'SIZE_15000_PLUS';
export const enterpriseLeadOrganizationSizeCategory: Record<
  EnterpriseLeadOrganizationSizeCategory,
  EnterpriseLeadOrganizationSizeCategory
> = {
  SIZE_1_500: 'SIZE_1_500',

  // Org Size of 501-1000
  SIZE_501_1000: 'SIZE_501_1000',

  // Org Size of 1001-5000
  SIZE_1001_5000: 'SIZE_1001_5000',

  // Org Size of 5001-15000
  SIZE_5001_15000: 'SIZE_5001_15000',

  // Org Size of 15000+
  SIZE_15000_PLUS: 'SIZE_15000_PLUS',
};

export type EnterpriseLeadBillingAddress = {
  state?: string;
  country?: string;
  postalCode?: string;
};

const mapOrgType = (key: EnterpriseLeadOrganizationType | undefined): EnterpriseLeadOrganizationType | undefined => {
  if (key === enterpriseLeadOrganizationType.NON_PROFIT) {
    return enterpriseLeadOrganizationType.GOVERNMENT;
  } else {
    return key;
  }
};

export const createProspectiveEnterpriseLead = ({
  userData,
  organizationName,
  userLimit,
  coursesPerUserLimit,
  billingType,
  role,
  source,
  organizationType,
  organizationSizeCategory,
  transactionType,
}: {
  userData: UserData;
  organizationName: string;
  userLimit: number;
  coursesPerUserLimit: number;
  billingType: BillingTypeValues;
  role: string;
  source: EnterpriseLeadSource;
  transactionType?: EnterpriseLeadTransactionType;
  organizationType?: EnterpriseLeadOrganizationType;
  organizationSizeCategory?: EnterpriseLeadOrganizationSizeCategory;
}) => {
  if (!userData.email_address) {
    return null;
  }

  const data = {
    transactionType,
    contact: {
      email: userData.email_address,
      fullName: userData.full_name,
      userId: userData.id,
      title: role,
    },
    leadType: 'SELF_SERVE',
    source,
    organization: {
      name: organizationName,
      type: mapOrgType(organizationType),
      sizeCategory: organizationSizeCategory,
    },
    properties: {
      typeName: 'selfServeProperties',
      definition: {
        contractLimits: {
          userLimit,
          coursesPerUserLimit,
        },
        billingType,
      },
    },
  };

  const uri = new URI().addQueryParam('skipStore', 'false');
  if (userData.email_address === '') {
    uri.addQueryParam('skipZapier', 'true');
  }

  // Show log for internal users
  if (userData.email_address.includes('coursera.org')) {
    console.log(`[MOCK] createProspectiveEnterpriseLead: data = ${JSON.stringify(data)}, url=${uri.toString()}`);
    return null;
  }

  return enterpriseLeadsAPI.post(uri.toString(), { data });
};

export const createEnterpriseLead = ({
  cart,
  enterpriseContractInfo,
  acv,
  thirdPartyOrganization,
  userData,
  billingAddress,
  organizationType,
  organizationSizeCategory,
  transactionType,
}: {
  cart: CartsV2;
  enterpriseContractInfo: EnterpriseContractInfo;
  acv: string;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
  userData: UserData;
  transactionType?: EnterpriseLeadTransactionType;
  billingAddress?: EnterpriseLeadBillingAddress;
  organizationType?: EnterpriseLeadOrganizationType;
  organizationSizeCategory?: EnterpriseLeadOrganizationSizeCategory;
}) => {
  if (!userData.email_address) {
    return null;
  }

  const { enterpriseContractId } = enterpriseContractInfo;
  const {
    limits: { userLimit, coursesPerUserLimit },
  } = enterpriseContractInfo.properties.definition;
  const paymentTotal = String(cart.totalCartAmount);
  const { currencyCode } = cart;

  const data = {
    transactionType,
    contact: {
      email: userData.email_address,
      fullName: userData.full_name,
      userId: userData.id,
    },
    leadType: 'SELF_SERVE',
    source: 'WEB_ENABLED_SALES',
    organization: {
      name: thirdPartyOrganization && thirdPartyOrganization.name,
      type: mapOrgType(organizationType),
      sizeCategory: organizationSizeCategory,
    },
    billingAddress,
    properties: {
      typeName: 'selfServeProperties',
      definition: {
        contractLimits: {
          userLimit,
          coursesPerUserLimit,
        },
        contractPrice: {
          currencyCode,
          paymentTotal,
          annualContractValue: acv,
        },
        contractId: enterpriseContractId,
      },
    },
  };

  // Show log for internal users
  if (userData.email_address.includes('coursera.org')) {
    console.log('[MOCK] createEnterpriseLead: data = ', data);
    return null;
  }

  return enterpriseLeadsAPI.post('', { data });
};

export const trackEnterpriseContractPayment = ({
  cart,
  enterpriseContractInfo,
  acv,
  hasUserPaidBefore,
}: {
  cart: CartsV2;
  enterpriseContractInfo: EnterpriseContractInfo;
  acv: string;
  hasUserPaidBefore?: boolean;
}) => {
  const { enterpriseContractId } = enterpriseContractInfo;
  const {
    limits: { userLimit, coursesPerUserLimit },
  } = enterpriseContractInfo.properties.definition;
  const paymentTotal = String(cart.totalCartAmount);
  const { currencyCode } = cart;

  const gtmData = {
    isFirstTimePayer: !hasUserPaidBefore,
    contractLimits: {
      userLimit,
      coursesPerUserLimit,
    },
    contractPrice: {
      currencyCode,
      paymentTotal,
      annualContractValue: acv,
    },
    contractId: enterpriseContractId,
  };
  return adsTracker.trackEnterpriseContractPayment(gtmData);
};
